import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { HiOutlineCalendar } from "react-icons/hi";
import { CustomButton } from "./CustomButton";
import { useCardPreview } from "../../contexts/CardPreviewContext";
import type { BusinessCardBooking } from "../../types/BusinessCardTypes";
import {
  usePublicBookingPage,
  usePublicBookingType,
} from "../../hooks/agenda/publicBooking";
import {
  captureBookingUtmParams,
  withUtmParams,
} from "../pages/customRegister/utmCapture";

interface BookingLinkButtonProps {
  booking?: BusinessCardBooking;
  entityIdentification: string;
  memberId?: string;
  className?: string;
  variant?: "primary" | "secondary";
}

export const bookingPath = (
  entityIdentification: string,
  typeId: string,
  memberId?: string
) => `/booking/${entityIdentification}/${typeId}${memberQuery(memberId)}`;

const memberQuery = (memberId?: string) =>
  memberId ? `?member=${encodeURIComponent(memberId)}` : "";

/** The entity's booking page, showing only the services that offer `memberId` when given. */
export const bookingPagePath = (
  entityIdentification: string,
  memberId?: string
) => `/booking/${entityIdentification}${memberQuery(memberId)}`;

/**
 * Without a pinned type, the button only shows once the person's services are known to be some; with one, once that
 * type is known to be still bookable with the person (the card itself may be cached longer). The card's UTM
 * parameters go with it, kept for the entity's booking pages like those of a visit to them.
 */
const BookingLinkButton: React.FC<BookingLinkButtonProps> = ({
  booking,
  entityIdentification,
  memberId,
  className = "w-full",
  variant = "secondary",
}) => {
  const { t } = useTranslation("clientView");
  const isPreview = useCardPreview();
  const { search } = useLocation();
  const utm = useMemo(
    () =>
      isPreview || !entityIdentification
        ? {}
        : captureBookingUtmParams(entityIdentification, search),
    [entityIdentification, isPreview, search]
  );
  const typeId = booking?.type_id;
  const offersAll = !!booking?.enabled && !typeId && !!entityIdentification;
  const { data: services } = usePublicBookingPage(
    entityIdentification,
    memberId,
    { enabled: offersAll }
  );
  const { data: pinned } = usePublicBookingType(
    entityIdentification,
    booking?.enabled && typeId ? typeId : undefined
  );
  const pinnedBookable =
    !!pinned &&
    (!memberId ||
      pinned.members.some((m) => m.user_identification === memberId));

  if (!booking?.enabled || !entityIdentification) return null;
  if (!typeId && !services?.types.length) return null;
  if (typeId && !pinnedBookable) return null;
  const path = withUtmParams(
    typeId
      ? bookingPath(entityIdentification, typeId, memberId)
      : bookingPagePath(entityIdentification, memberId),
    utm
  );

  return (
    <CustomButton
      variant={variant}
      size="md"
      className={className}
      icon={<HiOutlineCalendar size={18} />}
      title={t("bookingTunnel.cardCta") as string}
      disabled={isPreview}
      tabIndex={isPreview ? -1 : undefined}
      onClick={isPreview ? undefined : () => window.location.assign(path)}
    />
  );
};

export default BookingLinkButton;
